<template>
  <div :id="id" class="quick-quote-shipping-point-location-edit" tabindex="-1">
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, errors, inputId }"
      :errors="v$.city.$errors"
      :input-id="`${id}-city`"
      :label="t('address.city')"
      :help-text="t('quickQuote.shippingPointInstructions')"
      class="w-3"
    >
      <QuickQuoteLocationQuickSearch
        :id="inputId"
        v-model="v$.city.$model"
        v-bind="ariaProps"
        :errors="errors"
        :class="inputClasses"
        @address-book-select="emit('address-book-select', $event)"
        @clear="onClearCity"
      />
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputId }"
      :input-id="`${id}-state`"
      :label="t('address.state')"
      class="w-3"
    >
      <InputRegion
        :id="inputId"
        v-model="internalValue.state"
        v-bind="ariaProps"
        :country="internalValue.country"
        :disabled="isFiconLocation"
      />
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :errors="v$.postalCode.$errors"
      :input-id="`${id}-postal-code`"
      :label="t('address.postalCode')"
      class="w-3"
    >
      <RygenDropdown
        :id="inputId"
        :model-value="v$.postalCode.$model"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :disabled="isFiconLocation"
        :loading="suggestions.loading"
        :options="suggestions.data"
        :option-label="autocompleteOptionLabel"
        append-to="body"
        editable
        :placeholder="t('address.postalCode')"
        @input="onAddressSearch"
        @before-show="onAddressSearch"
        @change="onItemSelected($event.value)"
        @update:model-value="onUpdatePostalCode"
      >
        <template #option="{ option }: { option: Address | string }">
          <span v-if="typeof option === 'string'">
            {{ option }}
          </span>
          <NamedAddress v-else :address="option" />
        </template>
      </RygenDropdown>
    </InputWrapper>

    <InputWrapper
      v-slot="{ ariaProps, inputId }"
      :input-id="`${id}-country`"
      :label="t('address.country')"
      class="w-3"
    >
      <CountryDropdown
        :id="inputId"
        v-model="internalValue.country"
        v-bind="ariaProps"
        :disabled="isFiconLocation"
      />
    </InputWrapper>
  </div>
</template>

<script lang="ts" setup>
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import { computed, ref, Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  requiredIf,
  useModelValue,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { Address, Loadable, Location } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import { EmbeddableLocation } from '@/model';

import QuickQuoteLocationQuickSearch from '@/components/quickQuote/QuickQuoteLocationQuickSearch.vue';
import NamedAddress from '@rygen/rygen-ui/components/address/NamedAddress.vue';
import CountryDropdown from '@rygen/rygen-ui/components/form/CountryDropdown.vue';
import InputRegion from '@rygen/rygen-ui/components/form/InputRegion.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';

const props = defineProps({
  modelValue: {
    type: EmbeddableLocation,
    default: () => new EmbeddableLocation(),
  },
  id: {
    type: String,
    required: true,
  },
  hasAttemptedResolution: {
    type: Boolean,
    default: false,
  },
});
const { modelValue, id, hasAttemptedResolution } = toRefs(props);
const emit = defineEmits<{
  (event: 'address-book-select', location: Location): void;
  (event: 'clear'): void;
  (event: 'update:model-value', modelValue: EmbeddableLocation): void;
}>();

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<EmbeddableLocation>) =>
    new EmbeddableLocation(opts),
});
const rules = {
  city: {
    quickQuoteLocation: requiredIf(() =>
      !hasAttemptedResolution.value
        ? !internalValue.value.city &&
          !internalValue.value.state &&
          !internalValue.value.postalCode
        : !internalValue.value.city || !internalValue.value.state,
    ),
  },
  postalCode: {
    quickQuoteLocation: requiredIf(() =>
      !hasAttemptedResolution.value
        ? !internalValue.value.city &&
          !internalValue.value.state &&
          !internalValue.value.postalCode
        : !internalValue.value.postalCode,
    ),
  },
};

const { v$ } = useValidations(internalValue, rules);

const suggestions: Ref<Loadable<Address[]>> = ref({ loading: false });
const cancelRequest: Ref<CancelTokenSource | undefined> = ref(undefined);

const isFiconLocation = computed(() => !!internalValue.value.locationId);

const autocompleteOptionLabel = (opt: Address | string) => {
  return typeof opt === 'string' ? opt : (opt.postalCode ?? '');
};

const cancelExistingAutocompleteRequest = () => {
  if (cancelRequest.value) {
    onAddressSearch?.cancel();
    cancelRequest.value.cancel();
    suggestions.value.data = undefined;
  }
  cancelRequest.value = axios.CancelToken.source();
};

const getAutocompleteSuggestions = async () => {
  try {
    suggestions.value.loading = true;
    cancelExistingAutocompleteRequest();

    if (!internalValue.value.postalCode) {
      return;
    }

    const address = new Address({
      postalCode: internalValue.value.postalCode,
      country: internalValue.value.country,
    });
    const enrichedAddresses =
      await api.shippingPoint.getAutocompleteZipSuggestions(
        address,
        cancelRequest.value,
      );
    suggestions.value.data = enrichedAddresses
      .map((e) => e.address)
      .filter(notNull);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching autocomplete suggestions', e);
  } finally {
    suggestions.value.loading = false;
  }
};
const onAddressSearch = debounce(getAutocompleteSuggestions, 500);

const onItemSelected = (item: Address) => {
  // If the user selects the first item, which is always the current input,
  // bail out
  if (!suggestions.value.data?.includes?.(item)) {
    return;
  }
  internalValue.value = EmbeddableLocation.fromAddress(item);
};

const onUpdatePostalCode = (newVal: string | Address) => {
  internalValue.value.postalCode =
    typeof newVal === 'string' ? newVal : newVal.postalCode;
};

const onClearCity = () => {
  if (isFiconLocation.value) {
    emit('clear');
  } else {
    internalValue.value.city = undefined;
  }
};
</script>

<style>
.quick-quote-shipping-point-location-edit {
  display: flex;
  gap: var(--gap-1);
}
</style>
